import { useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  useLazyExportWarehouseProductsToExcelQuery,
  type ExportWarehouseProductsToExcelResponse,
} from '@app/api/Warehouses';
import { ADD_PRODUCT_PATH, FRONT_WARNING, XLSX } from '@app/constants';
import { useNewUserAccessRights, useUserPrint } from '@app/hooks';
import { getWarehouseIdFromSearchParams } from '@app/pages/Warehouses/Products/utils';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';

export const useProductsHeader = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showNotification } = useNotification();
  const {
    params: { ActiveBranchId },
  } = useUserPrint();

  const {
    userAccessRights: {
      Products: { IsProductCreate },
      Warehouse: { IsExportWarehouseProductsToExcel },
    },
  } = useNewUserAccessRights();

  const [exportWarehouseProductsToExcel, { isFetching: isExportLoading }] =
    useLazyExportWarehouseProductsToExcelQuery();

  const warehouseId = useMemo(
    () => getWarehouseIdFromSearchParams(searchParams),
    [searchParams]
  );

  const goToAddProduct = useCallback(() => {
    navigate(ADD_PRODUCT_PATH);
  }, [navigate]);

  const handleFileData = useCallback(
    (file: ExportWarehouseProductsToExcelResponse) => {
      if (!file.Data) {
        showNotification('При получении файла произошла ошибка', FRONT_WARNING);
        return;
      }

      const link = document.createElement('a');
      link.href = `data:${XLSX};base64,${file.Data}`;
      link.download = file.Name || 'Товары склада.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    [showNotification]
  );

  const exportWarehouseProductsToExcelHandler = useCallback(async () => {
    if (isExportLoading) return;

    if (!warehouseId) {
      showNotification(
        'Пожалуйста, выберите склад для экспорта',
        FRONT_WARNING
      );
      return;
    }

    try {
      const file = await exportWarehouseProductsToExcel({
        ActiveBranchId,
        warehouseId,
      }).unwrap();

      handleFileData(file);
    } catch (error) {
      showNotification(error);
    }
  }, [
    ActiveBranchId,
    exportWarehouseProductsToExcel,
    handleFileData,
    isExportLoading,
    showNotification,
    warehouseId,
  ]);

  return {
    goToAddProduct,
    IsProductCreate,
    IsExportWarehouseProductsToExcel,
    exportWarehouseProductsToExcelHandler,
    isExportLoading,
  };
};
